import { Component } from 'preact';
import { Id, Maybe, Some } from '../../common/data.js';
import {
	apply,
	debounce,
	findMatch,
	isEmpty,
	isEmptyOrNone,
	isIn,
	isSome,
	lengthEq,
	logRejection,
	maybe,
	notEmpty,
	objectEntries,
	sleep,
	unique,
} from '../../common/util.js';
import { Checkbox } from './checkbox.js';
import { Icon } from './icons.js';
import { Popup } from './popup.js';
import { ProductCard } from './product-card.js';
import { ContentBlock, ContentContext } from './content.js';
import {
	ComputedProduct,
	ProductCategory,
	ProductCategoryDimension,
	ProductSortType,
	topLevelDimensions,
} from '../../common/data/products.js';
import { SiteContext } from '../context.js';
import { listProducts, ListProductsParams } from '../lib/api.js';
import { classNames, getEventValue, submitQueue } from '../lib/util.js';
import { Input } from './input.js';
import { ContentExtras } from '../../common/data/content.js';

const sortTypes: Record<ProductSortType, string> = {
	popularity: 'Popularity',
	relevance: 'Relevance',
	priceLow: 'Price: Low to High',
	priceHigh: 'Price: High to Low',
};

export interface ProductPageInit extends Some<ListProductsParams> {
	products: ComputedProduct[];
	category?: Maybe<ProductCategory>;
	validCategories: Id<ProductCategory>[];
}

export interface ProductPageProps extends ProductPageInit {
	categories: ProductCategory[];
	dimensions: ProductCategoryDimension[];
	freeShipping: number;
	extras: ContentExtras;
}

export interface ProductPageState extends ProductPageInit {
	loading?: boolean;
	collapsed?: string[];
	filterInfoPopup?: string;
	sidebarHide?: boolean;
	hiddenClass?: string;
	filterCategoryPopup?: boolean;
}

export class ProductsPage extends Component<ProductPageProps, ProductPageState> {

	static contextType = SiteContext;
	declare context: SiteContext;
	
	requested: Some<ListProductsParams>;
	loaded: ListProductsParams;

	constructor(props: ProductPageProps) {
		super(props);
		const { products, filter, query, sort, category, validCategories } = props;
		const params = { filter, query, sort };
		this.state = {
			products,
			filter,
			query,
			sort,
			category,
			validCategories,
			collapsed: category ? [category.dimension] : [],
		};
		this.requested = params;
		this.loaded = params;
	}

	componentDidMount() {
		this.updateWindow(this.requested);
	}
	
	@submitQueue
	async loadProducts() {
		
		const params = this.requested;
		if (params == this.loaded) return this.setState({ loading: false });
		
		const { products, validCategories } = await listProducts(params);
		
		this.loaded = params;
		this.setState({
			products,
			validCategories,
			loading: params != this.requested,
		});
		
		this.updateWindow(params);
		
		await sleep(100);
	}
	
	updateWindow(params: Some<ListProductsParams>) {
		
		const { categories } = this.props;
		
		let fArr = objectEntries(params.filter ?? {}).filter(e => notEmpty(e[1]));
		const sCid = lengthEq(fArr, 1) && lengthEq(fArr[0][1], 1) ? fArr[0][1][0] : null;
		const qp = params.query?.trim() || null;
		const single = isSome(sCid) && isEmptyOrNone(qp) ? findMatch(categories, { url: sCid }) ?? findMatch(categories, { id: sCid }) : null;
		if (single) fArr = fArr.filter(([dim]) => dim != single.dimension);
		
		this.setState({ category: single });
		
		const query = isEmpty(fArr) && !qp ? null : new URLSearchParams([
			...fArr
				.toSorted(([a], [b]) => a.localeCompare(b))
				.map(([dim, cids]) => [dim, cids?.toSorted().join(',') ?? '']),
			...qp ? [['q', qp]] : [],
		]);
		
		const path = (
			maybe(single, c => (isIn(c.dimension, topLevelDimensions) ? '/' : '/products/') + (c.url || c.id).toLowerCase())
			?? `/products`
		);
		const url = path + (query ? `?${query}` : '');
		const title = (single ? single.name + ' – ' : '') + this.context.domain.value.brand_name;
		if (location.pathname + location.search != url) {
			document.title = title;
			history.replaceState({}, title, url);
		}
	}
	
	updateParams(update: Partial<ListProductsParams>) {
		this.requested = { ...this.requested, ...update };
		this.loadProducts().catch(logRejection);
		this.setState({
			...update,
			loading: true,
		});
	}

	resetFilter = () => {
		this.updateParams({ filter: {} });
	};

	toggleSidebar = () => {
		this.setState({
			...this.state,
			sidebarHide: !this.state.sidebarHide,
			hiddenClass: '',
		});
	};

	toggleFilter = (dim: string, it: string, value: boolean) => {
		const { filter } = this.state;
		const arr = (
			value ? unique([...(filter?.[dim] || []), it].toSorted())
			: filter?.[dim] ? apply(filter[dim].filter(cid => cid != it), arr => notEmpty(arr) ? arr : undefined)
			: undefined
		);
		if (this.state.collapsed?.includes(dim)) this.setState({
			collapsed: this.state.collapsed.filter(s => s != dim),
		});
		this.updateParams({
			filter: {
				...filter,
				[dim]: arr,
			},
		});
	};
	
	updateQuery = debounce(
		(str: string) => this.updateParams({ query: str.trim() || null }),
		250
	);

	toggleSection = (section: string) => () => {
		const collapsed = this.state.collapsed || [];
		if (collapsed.includes(section)) this.setState({ collapsed: [...collapsed.filter(sect => sect !== section)] });
		else this.setState({ collapsed: [...collapsed, section] });
	};


	showFilterCategoryPopup = () => this.setState({ filterCategoryPopup: !this.state.filterCategoryPopup });
	hideFilterCategoryPopup = () => this.setState( { filterCategoryPopup: undefined });
	showFilterInfoPopup = (dimension: string) => this.setState({ filterInfoPopup: dimension });
	hideFilterInfoPopup = () => this.setState({ filterInfoPopup: undefined });

	render({ dimensions, categories, freeShipping, extras }: ProductPageProps, { filter, products, loading, category, collapsed, filterInfoPopup, sidebarHide, hiddenClass, filterCategoryPopup, query, validCategories }: ProductPageState) {
		
		const domain = this.context.domain.value;
		const { toggleFilter, resetFilter, toggleSection } = this;

		const isClient = typeof window == 'object';
		const validCats = new Set(validCategories);

		return <div class="products-page">
			<div class="products-page__header">
				{`< Free U.S. Shipping for orders $${freeShipping}+ >`}
			</div>
			<div className="products-page__nav">
				<button className={classNames('products-page__nav-btn', '--hide')} onClick={ () => this.toggleSidebar()}>
					<span>{`${sidebarHide ? `Show` : `Hide`}`} Filters</span>
					<Icon name="filter"/>
				</button>
				<button className={classNames('products-page__nav-btn', '--filter')} onClick={ () => this.showFilterCategoryPopup()}>
					<Icon name="filter"/>
					<span>Filters</span>
				</button>
				<label class="products-page__nav-search">
					<Icon name="search" title="Search products" />
					<Input value={query ?? ''} handler={this.updateQuery} />
					{query && <button onClick={() => this.updateQuery('')}>
						<Icon name="close" title="Clear" />
					</button>}
				</label>
				<span className={classNames('products-page__nav-btn', '--sortby')}>
					<select value={this.state.sort ?? 'relevance'} onChange={ (e) => this.updateParams({ sort: getEventValue(e) as ProductSortType })}>
						{objectEntries(sortTypes).map(([key, label]) => <option value={key}>{label}</option>)}
					</select>
				</span>
			</div>
			<div class="products-page__body">
				<aside class={`products-page__filters ${sidebarHide ? `products-page__filters--d_none` : ``}`}>
					{dimensions.map((dim, i) => [
						<div class={`products-page__filter-group ${collapsed?.includes(dim.id) ? 'products-page__filter-group--collapsed': ''}`}>
							<div class="products-page__filters-header">
								<button class="products-page__filter-group-btn" onClick={toggleSection(dim.id)}>{collapsed?.includes(dim.id) ? <Icon name="plus" /> : <Icon name="minus" />}</button>
								<h3 class="products-page__filter-group-title">{dim.name}</h3>
								{dim.description?.trim() && <button class="products-page__filter-group-info" onClick={() => this.showFilterInfoPopup(dim.id)}>
									<Icon name="question" />
								</button>}
								{!i && maybe(filter, fmap => !!Object.keys(fmap).length) && <button class="products-page__reset-filters" title="Reset filters" onClick={resetFilter}>Clear all</button>}
							</div>
							{categories.filter(cat => cat.dimension === dim.id).map(it => <Checkbox
								id={it.id}
								label={apply(it.name || it.id, txt => isClient ? txt : <a href={`/products?${dim.id}=${it.id}`}>{txt}</a>)}
								checked={filter?.[dim.id]?.includes(it.id)}
								classes={[!validCats.has(it.id) && '--empty']}
								onChange={(val:boolean) => toggleFilter(dim.id, it.id, val)}
							/>)}
						</div>,
						<hr />,
					])}
				</aside>
				<main className="products-page__main">

					{category && <div class="products-page__cat-info">
						<h1>{category.title ?? category.name}</h1>
						{category.description && <ContentContext.Provider value={{
							target: ['web'],
							domain,
							website: `https://${domain.host}`,
							extras,
							link: s => s,
							image: s => s,
						}}>
							<ContentBlock item={category.description} />
						</ContentContext.Provider>}
					</div>}
					
					{isEmpty(products) && <div class="products-page__cat-info">
						<h1>No products found</h1>
						<p>Sorry, we don't have any products matching all of the selected filters.</p>
						<p>Click one of the links below to see all products in the selected category:</p>
						<ul>
							{categories.filter(c => filter?.[c.dimension]?.includes(c.id)).map(c => <li>
								<a href={
									topLevelDimensions.includes(c.dimension)
										? `/${c.url ?? c.id}`
										: `/products/${c.url ?? c.id}`
								}>{c.name}</a>
							</li>)}
						</ul>
					</div>}

					<div class={classNames('products-page__products-block', loading && '--loading')}>
						{products.map((p, i) => <ProductCard
							product={p}
							key={p.id}
							domain={this.context.domain.value}
							position={i}
						/>)}
					</div>

				</main>
			</div>
			{maybe(filterCategoryPopup, did => <Popup close={this.hideFilterCategoryPopup}>
				{dimensions.map((dim, i) => [
					<div class={`products-page__filter-group ${collapsed?.includes(dim.id) ? 'products-page__filter-group--collapsed': ''}`}>
						<div class="products-page__filters-header">
							<button class="products-page__filter-group-btn" onClick={toggleSection(dim.id)}>{collapsed?.includes(dim.id) ? <Icon name="plus" /> : <Icon name="minus" />}</button>
							<h3 class="products-page__filter-group-title">{dim.name}</h3>
							{dim.description?.trim() && <button class="products-page__filter-group-info" onClick={() => this.showFilterInfoPopup(dim.id)}>
								<Icon name="question" />
							</button>}
							{!i && maybe(filter, fmap => !!Object.keys(fmap).length) && <button class="products-page__reset-filters" title="Reset filters" onClick={resetFilter}>Clear all</button>}
						</div>
						{categories.filter(cat => cat.dimension === dim.id).map(it => <Checkbox id={it.id} label={it.name || it.id} checked={filter?.[dim.id]?.includes(it.id) || false} onChange={(val:boolean) => toggleFilter(dim.id, it.id, val)}/>)}
					</div>,
					<hr />,
				])}
			</Popup>)}
			{maybe(filterInfoPopup, did => <Popup close={this.hideFilterInfoPopup}>
				<div class="products-page__info-popup-body">
					{findMatch(dimensions, { id: did })!.description?.trim().split(/\n+/).map(p => <p>{p}</p>)}
				</div>
			</Popup>)}
		</div>;
	}
}

